import { useState } from 'react';
import Icon from '../../../../components/Icon/Icon';

const WhatsAppBillingDataTable = ({ data, loadingData, onRefresh }) => {
  const [expandedCountries, setExpandedCountries] = useState(new Set());

  const toggleCountryExpansion = (countryCode) => {
    const newExpanded = new Set(expandedCountries);
    if (newExpanded.has(countryCode)) {
      newExpanded.delete(countryCode);
    } else {
      newExpanded.add(countryCode);
    }
    setExpandedCountries(newExpanded);
  };

  const renderCountryFlag = (countryCode) => {
    // For now, we'll use a simple colored circle. Later we can add actual flag components
    const flagColors = {
      'MY': '#DC143C', // Malaysia red
      'ID': '#FF0000', // Indonesia red
      'SG': '#FF0000', // Singapore red
      'TH': '#FF0000', // Thailand red
      'VN': '#FF0000', // Vietnam red
      'PH': '#0038A8', // Philippines blue
      'US': '#B22234', // USA red
      'GB': '#012169', // UK blue
      'AU': '#012169', // Australia blue
      'IN': '#FF9933'  // India saffron
    };

    return (
      <div style={{
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        backgroundColor: flagColors[countryCode] || '#9CA8BB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        {countryCode}
      </div>
    );
  };

  const renderTableHeader = () => (
    <div style={{
      display: 'flex',
      alignSelf: 'stretch',
      backgroundColor: '#F1F6FF',
      borderBottom: '0.2px solid #9CA8BB'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'stretch',
        gap: '8px',
        padding: '8px 16px',
        flex: 1,
        borderRight: '0.2px solid #9CA8BB'
      }}>
        <span style={{
          fontFamily: 'Poppins',
          fontWeight: '600',
          fontSize: '14px',
          lineHeight: '1.5em',
          letterSpacing: '2%',
          textTransform: 'uppercase',
          color: '#344054'
        }}>
          Conversation Type
        </span>
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'stretch',
        gap: '33px',
        padding: '8px 9px',
        flex: 1,
        borderRight: '0.2px solid #9CA8BB'
      }}>
        <span style={{
          fontFamily: 'Poppins',
          fontWeight: '600',
          fontSize: '14px',
          lineHeight: '1.5em',
          letterSpacing: '2%',
          textTransform: 'uppercase',
          color: '#344054',
          width: '98px'
        }}>
          Volumes
        </span>
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        flex: 1,
        borderRight: '0.2px solid #9CA8BB',
        position: 'relative'
      }}>
        <span style={{
          fontFamily: 'Poppins',
          fontWeight: '600',
          fontSize: '14px',
          lineHeight: '1.5em',
          letterSpacing: '2%',
          textTransform: 'uppercase',
          color: '#344054'
        }}>
          Est. Cost/Message
        </span>
        <Icon
          name="info"
          color="#737C8A"
          width={16}
          height={16}
          style={{ position: 'absolute', right: '16px' }}
        />
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        flex: 1,
        height: '58px'
      }}>
        <span style={{
          fontFamily: 'Poppins',
          fontWeight: '600',
          fontSize: '14px',
          lineHeight: '1.5em',
          letterSpacing: '2%',
          textTransform: 'uppercase',
          color: '#344054'
        }}>
          Total Charges
        </span>
      </div>
    </div>
  );

  const renderTableRow = (conversationType) => (
    <div key={conversationType.category} style={{
      display: 'flex',
      alignItems: 'center',
      alignSelf: 'stretch',
      backgroundColor: '#FFFFFF',
      borderBottom: '0.2px solid #9CA8BB'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'stretch',
        gap: '8px',
        padding: '16px',
        flex: 1,
        borderRight: '0.2px solid #9CA8BB'
      }}>
        <span style={{
          fontFamily: 'Poppins',
          fontWeight: '400',
          fontSize: '13px',
          lineHeight: '1.5em',
          color: '#344054'
        }}>
          {formatConversationType(conversationType.category)}
        </span>
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'stretch',
        gap: '8px',
        padding: '16px',
        flex: 1,
        borderRight: '0.2px solid #9CA8BB'
      }}>
        <span style={{
          fontFamily: 'Poppins',
          fontWeight: '400',
          fontSize: '14px',
          lineHeight: '1.5em',
          color: '#344054'
        }}>
          {conversationType.volume.toLocaleString()}
        </span>
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'stretch',
        gap: '8px',
        padding: '16px',
        flex: 1,
        borderRight: '0.2px solid #9CA8BB'
      }}>
        <span style={{
          fontFamily: 'Poppins',
          fontWeight: '400',
          fontSize: '14px',
          lineHeight: '1.5em',
          color: '#344054'
        }}>
          {conversationType.cost_per_message.toFixed(2)}
        </span>
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'stretch',
        gap: '8px',
        padding: '16px',
        flex: 1
      }}>
        <span style={{
          fontFamily: 'Poppins',
          fontWeight: '600',
          fontSize: '14px',
          lineHeight: '1.5em',
          color: '#344054'
        }}>
          {conversationType.cost.toFixed(1)}
        </span>
      </div>
    </div>
  );

  const renderCountrySection = (country) => {
    const isExpanded = expandedCountries.has(country.country_code);
    
    return (
      <div key={country.country_code} style={{
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch'
      }}>
        {/* Country Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          alignSelf: 'stretch',
          gap: '10px',
          padding: '25px 20px 15px',
          backgroundColor: '#FFFFFF',
          cursor: 'pointer'
        }}
        onClick={() => toggleCountryExpansion(country.country_code)}
        >
          {renderCountryFlag(country.country_code)}
          <h3 style={{
            fontFamily: 'Poppins',
            fontWeight: '600',
            fontSize: '18px',
            lineHeight: '1.5em',
            color: '#344054',
            margin: '0'
          }}>
            {country.country_name}
          </h3>
          <Icon
            name={isExpanded ? "expand_less" : "expand_more"}
            color="#9CA8BB"
            width={24}
            height={24}
            style={{ marginLeft: 'auto' }}
          />
        </div>

        {/* Table Content - Always visible for now, can be made collapsible later */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'stretch'
        }}>
          {renderTableHeader()}
          {country.conversation_types.map(conversationType => 
            renderTableRow(conversationType)
          )}
        </div>
      </div>
    );
  };

  if (!data || !data.countries || data.countries.length === 0) {
    return null;
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignSelf: 'stretch',
      gap: '0px'
    }}>
      {data.countries.map(country => renderCountrySection(country))}
      
      {/* Summary Section */}
      {data.summary && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 32px',
          backgroundColor: '#F8F9FA',
          borderTop: '1px solid #E9ECEF'
        }}>
          <div style={{
            fontFamily: 'Poppins',
            fontWeight: '600',
            fontSize: '14px',
            color: '#344054'
          }}>
            Total: {data.summary.total_countries} {data.summary.total_countries === 1 ? 'country' : 'countries'}
          </div>
          <div style={{
            display: 'flex',
            gap: '20px'
          }}>
            <div style={{
              fontFamily: 'Poppins',
              fontWeight: '500',
              fontSize: '14px',
              color: '#344054'
            }}>
              Total Volume: {data.summary.total_volume.toLocaleString()}
            </div>
            <div style={{
              fontFamily: 'Poppins',
              fontWeight: '600',
              fontSize: '14px',
              color: '#0BA9E0'
            }}>
              Total Cost: {data.summary.total_cost.toFixed(2)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to format conversation type names
function formatConversationType(category) {
  const typeNames = {
    'MARKETING': 'Marketing',
    'AUTHENTICATION': 'Authentication',
    'UTILITY': 'Utility',
    'SERVICE': 'Service'
  };
  
  return typeNames[category] || category;
}

export default WhatsAppBillingDataTable;